/*
 * The chat module's own slice of the client model. Core keeps `conversations`, `messages`, `users`
 * and `groups` — the vocabulary every module speaks — and hands each module one opaque entry under
 * `model.modules[<id>]` for what only it understands (modules.md §8, S0-PLAN R-O). Everything here
 * is per-person read state and ephemeral presence: nothing core has a field for.
 */

import { defineModuleSlice } from "../../../core/store/moduleSlice.ts"
import type { CursorState, MemberSelf } from "../wire/chat.gen.ts"
import type { ConversationView, MessageView } from "../wire/views.ts"

export const CHAT_MODULE_ID = "chat"

/**
 * One loaded thread, keyed by the id of its root. The root is the pane's head and travels on every
 * page of the thread (protocol.md §3.2), so a pane that has paged far into the past still knows
 * what it is a thread of; `rows` are the replies alone, oldest first.
 *
 * A thread window is always anchored at the newest reply — there is no `hasMoreAfter` — because a
 * pane is only ever opened at its tail and an arriving reply is therefore always an append.
 */
export interface ThreadWindow {
	root?: MessageView
	rows: MessageView[]
	hasMoreBefore: boolean
	status: "cold" | "loading" | "ready" | "error"
}

export interface ChatSlice {
	/** This person's own membership row per conversation; it never leaves its owner. */
	self: Record<number, MemberSelf>
	/** Read cursor and the unread/mention counts the server keeps. */
	cursors: Record<number, CursorState>
	/**
	 * The seq the "New messages" divider was created with, per conversation. It is pinned when
	 * the conversation is opened and never recomputed while it is on screen, so the divider stays
	 * where the reader left it instead of walking down with every arriving message.
	 */
	unreadAnchor: Record<number, number>
	/** conversation → user → timestamp of the last typing signal; entries expire after 5 s. */
	typing: Record<number, Record<number, number>>
	/**
	 * The same per thread: root id → user → timestamp. A thread is its own room with its own box
	 * (X-A1 A5), so its signals are kept apart from the conversation's — somebody answering in a
	 * thread is not somebody writing in the channel.
	 */
	threadTyping: Record<number, Record<number, number>>
	/** user → "online" | "away" | "offline". */
	presence: Record<number, string>
	/**
	 * Which way a conversation's window is growing while a page is in flight, so the list can say
	 * "Loading earlier messages …" at the top and nothing at all for a page that replaces the window.
	 */
	paging: Record<number, "older" | "newer">
	/**
	 * Per conversation, how many times its window was *replaced* — opened at the tail, around the
	 * first unread or around a permalink — since this slice was made. The surface lands once per
	 * replacement: a reconnect that reloads the window is a new landing, a page merged above or
	 * below it is not.
	 */
	opened: Record<number, number>
	/**
	 * Per conversation, the seq a permalink named whose message is no longer there: the window
	 * around it is loaded and the list marks the place (X-A2 D1). Cleared by the next page load.
	 */
	gone: Record<number, number>
	/** The threads whose replies are loaded, by root id; a root nobody opened is simply absent. */
	threads: Record<number, ThreadWindow>
	/**
	 * root id → the newest reply of that thread this reader need not be told about. Today it holds
	 * their own replies and nothing else: one's own message is never unread (UX §8 #9), and a reply
	 * lies outside the conversation's read cursor, so without this the footer's dot would light up
	 * on one's own thread the moment one answered in it.
	 */
	threadSeen: Record<number, number>
	/**
	 * The conversations an administrator reaches only as an administrator, by id, as
	 * `GET /api/chat/conversations/{id}` last answered: a channel or private channel they are not in,
	 * whose head and members they may see and change, and whose history they may not read. They never
	 * enter `model.conversations`, which is persisted and drawn as readable — a reload would open a
	 * timeline the server refuses. Kept for the visit only: no event reaches a reader without a row, so
	 * every write made from the view reads it again (`conversation/manage.ts`), and so does arriving
	 * at its address (`routes.tsx`). Joining moves the conversation out (`chat.membership.self`).
	 */
	outside: Record<number, ConversationView>
}

export function emptyChatSlice(): ChatSlice {
	return {
		self: {},
		cursors: {},
		unreadAnchor: {},
		typing: {},
		threadTyping: {},
		presence: {},
		paging: {},
		opened: {},
		gone: {},
		threads: {},
		threadSeen: {},
		outside: {},
	}
}

/**
 * What of this slice is worth a disk (`FrontendModule.dehydrate`, `core/persist`): this reader's own
 * membership rows, their read cursors and the replies of their own they have been told about — the
 * three fields that are about the *account* and answer a question before the socket does. Everything
 * else is about this moment or about what the server will hand over again on the next page.
 *
 * It is a projection and not a rule about size. The slice is rewritten whenever it changes identity,
 * which is on every typing frame, every presence frame and every read-state event — continuously,
 * while anybody is working — and it carried `threads`: every reply of every thread a pane had opened,
 * with its render tree, structure-cloned into IndexedDB per debounce tick. Persisting a thread window
 * buys nothing either, because a pane refetches its page when it opens (`ThreadPane`).
 *
 * The counterpart is `hydrateChatSlice` below, which already answers the other half of the same
 * question: a field the cache does not carry comes back empty rather than as `undefined`. Because it
 * does, dropping a field from what is written needs no `MODEL_VERSION` bump — a cache written by the
 * build before this one simply carries three fields that are now read back and three that are not.
 */
export function dehydrateChatSlice(
	slice: ChatSlice,
): Pick<ChatSlice, "self" | "cursors" | "threadSeen"> {
	return { self: slice.self, cursors: slice.cursors, threadSeen: slice.threadSeen }
}

/**
 * The plumbing core gives every module's slice (`core/store/moduleSlice.ts`): the slice out of a
 * model, the model with it replaced, and the slice read back off the disk.
 *
 * `unreadAnchor` belongs to the *visit*: it is the seq the "New messages" line is drawn from while a
 * conversation is on screen, pinned again the moment the reader arrives, so a restored one would
 * draw yesterday's line in a conversation that has been read and send `Ctrl+J` to it (X-B3 A3). It
 * is therefore never read back from the cache. `outside` belongs to the visit for a stronger reason:
 * it holds conversations whose history this reader may not read, and nothing a disk kept may stand in
 * for what the server answers today. `dehydrateChatSlice` never writes it; naming it here means a
 * cache that somehow held it is not believed either.
 */
export const CHAT_SLICE = defineModuleSlice(CHAT_MODULE_ID, emptyChatSlice, {
	perVisit: ["unreadAnchor", "outside"],
})

/**
 * A slice read back off the disk, as a new visit must see it (`FrontendModule.hydrate`, the warm
 * boot of `core/persist`). The module's other hydration is `hydrateChat` (state/bootstrap.ts), which
 * is the *server's* cold start; this is the disk's warm one, and the only place a slice nobody has
 * validated is read.
 */
export const hydrateChatSlice = CHAT_SLICE.hydrate
